<script lang="ts">
  import { onDestroy, onMount, tick, untrack } from 'svelte';
  import { isAsyncPace, type Avatar, type GameConfig } from '../shared/config';
  import { PostMatchPresentation } from './app/post-match-presentation.svelte';
  import { modeConfig, type GameMode } from '../shared/modes';
  import { profileRestricted, type ProfileBlockFeature } from '../shared/profile-blocks';
  import type { ServerMessage } from '../shared/protocol';
  import { applyConfigPatch, configEditability } from './app/config-patch';
  import { captureContext } from './app/confirmation-guard';
  import { FinishParticipation } from './app/finish-participation.svelte';
  import { hostRole } from './app/host-role';
  import { lobbyRulesDue } from './app/lobby-rules';
  import { destinationBlockFeature } from './app/profile-block-navigation';
  import { readyPrompt } from './app/ready-check';
  import { SoftenedPresence } from './app/softened-presence.svelte';
  import {
    backDestination,
    destination,
    explicitArchiveNavigation,
    transition,
    visibleNavigation,
    type NavigationEvent,
    type Page,
  } from './app/navigation';
  import { NavigationWorkflow } from './app/navigation-workflow.svelte';
  import { SCREEN_TITLES } from './app/screen-titles';
  import { Toasts } from './app/toasts.svelte';
  import { readInitialNavigation, readInitialRoute, reloadAtRoot } from './app/url-sync.svelte';
  import { Sfx } from './audio/sounds';
  import { finishConfirmation } from './content/modes';
  import { powerUpCue } from './game/chaos';
  import { GameplayPerformance } from './game/gameplay-performance';
  import GameScreen from './game/GameScreen.svelte';
  import RulesDialog from './onboarding/RulesDialog.svelte';
  import { RulesGate } from './onboarding/rules-gate.svelte';
  import WelcomeDialog from './onboarding/WelcomeDialog.svelte';
  import LeaderboardPanel from './panels/LeaderboardPanel.svelte';
  import PageNavigation from './panels/PageNavigation.svelte';
  import { installBrowserLifecycle } from './platform/browser-lifecycle';
  import { inviteUrl, shareInvite } from './platform/share';
  import type { BrowserStore } from './platform/storage';
  import { queryClient } from './queries/query-client';
  import { createArchiveListInvalidator, removePrivateGeneration } from './queries/results';
  import { refreshMyRooms } from './queries/rooms';
  import ArchivedResult from './screens/ArchivedResult.svelte';
  import CompetitiveScreen from './screens/CompetitiveScreen.svelte';
  import JoinRoomScreen from './screens/JoinRoomScreen.svelte';
  import LoadingScreen from './screens/LoadingScreen.svelte';
  import MenuScreen from './screens/MenuScreen.svelte';
  import ModeSetupScreen from './screens/ModeSetupScreen.svelte';
  import ReadyCheckDialog from './screens/ReadyCheckDialog.svelte';
  import RoomLobby from './screens/RoomLobby.svelte';
  import TurnLobby from './screens/TurnLobby.svelte';
  import WaitingScreen from './screens/WaitingScreen.svelte';
  import RoundResultsView from './screens/RoundResults.svelte';
  import { provideSession } from './session/context';
  import { createRoomSession, type MessageContext } from './session/room-session';
  import SettingsScreen from './settings/SettingsScreen.svelte';
  import { provideAppearance, type Appearance } from './themes/appearance.svelte';
  import ThemeBackground from './themes/ThemeBackground.svelte';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import Dialog from './ui/Dialog.svelte';
  import DialogHeader from './ui/DialogHeader.svelte';
  import ProfileBlockDialog from './ui/ProfileBlockDialog.svelte';
  import ToastRegion from './ui/ToastRegion.svelte';

  interface Props {
    store: BrowserStore;
    appearance: Appearance;
  }
  const props: Props = $props();
  // Host-owned singletons for the app's lifetime; capturing the initial values is intended.
  // svelte-ignore state_referenced_locally
  const { store, appearance } = props;

  // Theme subscriptions and the audio context share the root component's lifetime.
  provideAppearance(appearance);
  onDestroy(appearance.initialize());

  const rules = new RulesGate(store);
  onDestroy(rules.dispose);
  const toasts = new Toasts();
  onDestroy(() => toasts.dispose());
  const finish = new FinishParticipation();
  const presence = new SoftenedPresence();
  onDestroy(() => presence.dispose());

  const initialRoute = readInitialRoute();
  const navigationWorkflow = new NavigationWorkflow(readInitialNavigation(initialRoute), {
    session: () => $session,
    confirmLeave: () => confirmNavigation('Leave this round and return to the menu?', 'Leave Round', true),
    leave: () => session.leave(),
    detach: () => session.detach(),
    clearSelection,
    refreshRooms: () => void refreshMyRooms(queryClient),
    cancelAutoStart: () => session.cancelAutoStart(),
    clearJoinStatus: () => session.clearJoinStatus(),
  });
  const navigation = $derived(navigationWorkflow.current);
  let confirmation = $state<ConfirmDialog>();
  let gameScreen = $state<GameScreen>();
  let canPlay = $state(false);
  let welcomeOpen = $state(false);
  let attemptedBlockedFeature = $state<ProfileBlockFeature | null>(null);
  // Page sampling and one-shot round cues survive Settings and screen remounts.
  let gameplayPerformance = $state<GameplayPerformance>();
  let lastCountdown = $state(0);
  let visualRoundId = $state<string | null>(null);
  const postMatch = new PostMatchPresentation();

  const session = provideSession(
    createRoomSession(store, {
      explainRules: rules.explain,
      roomEntered(code) {
        store.roomCode = code;
        navigationWorkflow.replaceUrl(null, code);
      },
      archived(result) {
        navigationWorkflow.replaceUrl(result.roundId, result.roomCode);
      },
      message: handleMessage,
      roundStarted: () => Sfx.play('start'),
      disconnected(takenOver) {
        gameplayPerformance?.disconnected();
        if (takenOver) navigate({ type: 'join-required' }, 'replace');
      },
      joinRequired() {
        clearSelection();
        navigate({ type: 'join-required' }, 'replace');
      },
    }),
  );

  // Required onboarding sits alone: a retained Settings entry (e.g. after clearing user
  // data) stays in the navigation stack but its modal waits until welcome completes.
  const settingsOpen = $derived(navigation.kind === 'settings' && !welcomeOpen);
  const visible = $derived(visibleNavigation(navigation));
  const screen = $derived(destination(visible, $session));
  const explicitArchive = $derived(explicitArchiveNavigation(navigation));
  $effect(() =>
    postMatch.observe({
      results: $session.results,
      mode: $session.mode,
      revealAt: $session.revealAt,
      serverOffset: $session.serverOffset,
      visible: screen === 'results',
    }),
  );
  $effect(() => postMatch.run(screen === 'results' && postMatch.active));
  $effect(() => {
    const cue = postMatch.takeCue(screen === 'results', $session.results, $session.meId);
    if (cue) Sfx.play(cue);
  });
  const archivedRoundId = $derived(explicitArchive?.roundId ?? $session.archivedResult?.roundId ?? null);
  const backToMode = $derived(screen === 'results' && $session.mode !== 'daily');
  // Screens see a drop only after the notice delay, so a blip never re-dims a player or
  // flashes stand-in host controls; the server keeps the truthful list for expiry and claims.
  $effect(() => presence.observe($session.players));
  const shownPlayers = $derived(presence.soften($session.players));
  const asyncRoom = $derived(isAsyncPace($session.activeConfig ?? $session.config));
  // In a turn-taking room only your own running turn is interrupted by leaving the screen.
  const inOwnTurn = $derived(!asyncRoom || ($session.begun && !$session.finished));
  const role = $derived(hostRole(shownPlayers, $session.meId, !asyncRoom));
  const isHost = $derived(role !== 'guest');
  const readyCheckPrompt = $derived(readyPrompt($session.readyCheck, $session.players, $session.meId));
  const editability = $derived(configEditability($session.mode, isHost, $session.phase));
  const settingsEditable = $derived(editability === 'all');
  const participationIdentity = $derived(`${$session.roomCode}/${$session.meId}/${$session.roundId}`);
  const blockedDestination = $derived(
    destinationBlockFeature(
      screen,
      $session.mode,
      $session.hasSnapshot || $session.inSetupLobby,
      role === 'host',
    ),
  );
  const profileBlockOpen = $derived(
    profileRestricted($session, attemptedBlockedFeature ?? blockedDestination),
  );
  const featureScopedBlock = $derived($session.blockedFeatures !== null);

  $effect(() => {
    if (attemptedBlockedFeature !== null && !profileRestricted($session, attemptedBlockedFeature))
      attemptedBlockedFeature = null;
  });

  $effect(() => {
    finish.observe({
      identity: participationIdentity,
      finished: $session.finished,
      resultsShown: $session.phase === 'results',
    });
  });
  $effect(() => {
    if (screen === 'game') toasts.dismiss();
  });
  $effect(() => {
    const due = lobbyRulesDue({
      screen,
      mode: $session.mode,
      host: isHost,
      readyCheckOpen: $session.readyCheck !== null,
      resultsSettled: postMatch.settled,
      variant: $session.config.variant,
    });
    const key = `${$session.roomCode}:${due}`;
    // The gate's own state must not subscribe this effect; only the lobby inputs drive it.
    untrack(() => (due ? rules.offer(due, key) : rules.withdraw()));
  });
  $effect(() => {
    document.body.classList.toggle('no-scroll', screen === 'game');
  });
  let previousProfileGeneration = 0;
  $effect(() => {
    if ($session.profileGeneration !== previousProfileGeneration) {
      const oldGeneration = previousProfileGeneration;
      previousProfileGeneration = $session.profileGeneration;
      if (oldGeneration > 0) void removePrivateGeneration(queryClient, oldGeneration);
    }
  });
  const observeArchiveReady = createArchiveListInvalidator(queryClient);
  $effect(() => observeArchiveReady($session.resultLifecycle.archiveRoundId));

  onMount(() => {
    gameplayPerformance = new GameplayPerformance();
    return () => gameplayPerformance?.dispose();
  });

  function navigate(event: NavigationEvent, history: 'push' | 'replace' = 'push'): void {
    navigationWorkflow.navigate(event, history);
  }

  /** Snapshot navigation and identity before an await; see confirmation-guard. */
  function context() {
    return captureContext(() => ({ navigation, session: $session }));
  }

  function showToast(message: string, good = false): void {
    if (screen === 'game' && !settingsOpen) {
      gameScreen?.showMessage(message);
      return;
    }
    toasts.show(message, good);
  }

  async function finishParticipation(): Promise<void> {
    if (finish.finishing || !$session.ready || $session.spectator) return;
    const round = $session.roundId;
    if (!(await confirmNavigation(finishConfirmation($session.mode), 'Finish', true))) return;
    if (round !== $session.roundId || !$session.ready || $session.spectator || $session.finished) return;
    finish.begin(participationIdentity, () => session.finishRound());
  }

  async function joinRoom(resume = false): Promise<void> {
    navigate({ type: 'room' });
    // The code being joined is known now; the entry carries its URL before the server confirms.
    if ($session.roomCode) navigationWorkflow.replaceUrl(null, $session.roomCode);
    await enterRoomEntry(() => (resume ? session.resumeInvitation() : session.joinRoom()));
  }

  async function createRoom(): Promise<void> {
    navigate({ type: 'room' });
    await enterRoomEntry(() => session.createRoom());
  }

  /** A room entry that ends neither in the room nor in its archive steps back to its form. */
  async function enterRoomEntry(enter: () => Promise<void>): Promise<void> {
    const current = navigation;
    await enter();
    const entered = $session.joined || $session.archivedResult || $session.archivePendingRoundId;
    if (navigation === current && !entered)
      await navigationWorkflow.backTo(transition(navigation, { type: 'back' }));
  }

  function handleMessage(message: ServerMessage, previous: MessageContext): void {
    if (message.t === 'blocked') attemptedBlockedFeature = message.feature;
    if (message.t === 'state') applyState(previous);
    else if (message.t === 'error') showToast(message.msg);
    else {
      if (message.t === 'wordResult') gameplayPerformance?.acknowledged(message.roundId, message.w);
      if (
        message.t === 'wordResult' &&
        previous.submissionFeedback &&
        message.roundId === $session.roundId &&
        $session.phase === 'playing'
      )
        Sfx.play(!message.ok && message.reason === 'dup' ? 'duplicate' : previous.submissionFeedback);
      if (message.t === 'powerUp' && message.roundId === $session.roundId) {
        const cue = powerUpCue(message, $session.meId);
        if (cue) Sfx.play(cue);
      }
      gameScreen?.handleMessage(message, previous.submissionFeedback);
    }
  }

  function applyState(previousState: MessageContext): void {
    const previous = previousState.phase;
    if (previousState.resetInput) clearSelection();
    if ($session.phase !== 'countdown') lastCountdown = 0;
    if ($session.phase === 'lobby') {
      visualRoundId = null;
      if ($session.autoStart && !$session.inSetupLobby) {
        session.cancelAutoStart();
        void startRound();
      }
    } else if ($session.phase === 'countdown' || $session.phase === 'playing') {
      if ($session.phase === 'playing' && previous !== 'playing') Sfx.play('start');
      if (previous !== 'countdown' && previous !== 'playing') clearSelection();
    } else if ($session.phase === 'results') {
      // The round cue plays when the shared reveal reaches standings, keyed by
      // round so snapshots and archive readiness never replay it.
      if (previous !== 'results' && $session.results) postMatch.completed($session.results.roundId);
    }
  }

  function clearSelection(): void {
    gameScreen?.clearSelection();
  }

  function updateConfig(patch: Partial<GameConfig>): void {
    const base = screen === 'host' ? $session.hostConfig : $session.config;
    const result = applyConfigPatch(base, patch);
    if (!result.ok) {
      showToast(result.error);
      return;
    }
    if (screen === 'host') session.updateHostConfig(result.value.config);
    // The server merges patches; sending the whole stale snapshot could undo another control's edit.
    else if (editability !== 'none') session.updateRoomConfig(result.value.patch);
  }

  async function startRound(): Promise<void> {
    const before = context();
    const phase = $session.phase;
    // Any member may open the next Turns round from the completed checkpoint;
    // first-round start and Live Versus replay stay host-owned.
    const turnsContinue = asyncRoom && phase === 'results' && $session.results !== null && $session.joined;
    if (
      !(await rules.explain($session.config.variant)) ||
      !before.stillCurrent() ||
      phase !== $session.phase ||
      (!isHost && !turnsContinue)
    )
      return;
    Sfx.unlock();
    if (!session.startRound()) showToast('Not connected. Try again.');
    else if (turnsContinue) session.dismissResults();
  }

  /** Local lobby dismissal: hosts and guests leave the recap without moving the shared room. */
  function goLobby(): void {
    if (!$session.results || postMatch.gated) return;
    if (asyncRoom && $session.phase === 'playing') {
      session.dismissResults();
      return;
    }
    if ($session.phase !== 'results') return;
    if (asyncRoom) session.dismissResults();
    navigate({ type: 'results-lobby', roundId: $session.results.roundId });
  }

  /** Completed Daily exits its room before opening the leaderboard. */
  function openPostMatchLeaderboard(): void {
    exitRoom();
    navigate({ type: 'open', page: 'leaderboard' });
  }

  async function shareRoomLink(): Promise<void> {
    const url = inviteUrl($session.roomCode);
    const outcome = await shareInvite({
      url,
      title: 'Spellie',
      text: `Join my Spellie room ${$session.roomCode}`,
    });
    if (outcome === 'shared') showToast('Invite shared', true);
    else if (outcome === 'copied') showToast('Invite link copied', true);
    else if (outcome === 'failed') showToast('Could not share. Copy the URL manually.');
  }

  async function resetScores(): Promise<void> {
    const before = context();
    const accepted = await confirmation!.confirm('Reset everyone’s running totals?', {
      destructive: true,
      actionLabel: 'Reset Totals',
    });
    if (accepted && before.stillCurrent() && settingsEditable) session.resetScores();
  }

  function leaveRoom(): void {
    navigationWorkflow.exitHome('leave');
  }

  /** Home from a turn-taking room keeps the membership; the room stays listed on the menu. */
  function exitRoom(): void {
    navigationWorkflow.exitHome('by-pace');
  }

  /** Open one of this profile's turn-taking rooms from the menu. */
  function openRoom(code: string): void {
    navigate({ type: 'join-required' });
    session.setRoomInput(code);
    void joinRoom();
  }

  function beginTurn(): void {
    Sfx.unlock();
    if (!session.beginTurn()) showToast('Not connected. Try again.');
  }

  /** Return to the recap from the turn lobby while its round is still completed. */
  function viewResultsRecap(): void {
    session.recallResults();
    if ($session.phase === 'results') navigate({ type: 'view-results' });
  }

  function chooseAvatar(value: Avatar): void {
    session.setIdentity({ avatar: value });
    Sfx.play('select');
  }

  function blockAttempt(feature: ProfileBlockFeature): boolean {
    if (!profileRestricted($session, feature)) return false;
    attemptedBlockedFeature = feature;
    return true;
  }

  function chooseMode(value: GameMode): void {
    if (blockAttempt(value)) return;
    // Competitive rooms come from the queue, so the mode opens its page instead of a setup.
    if (value === 'competitive') {
      if ($session.joined) exitRoom();
      navigate({ type: 'open', page: 'competitive' });
      return;
    }
    session.selectSetupMode(value);
    navigate({ type: 'room' });
    void session.prepareSetup();
  }

  function openVersus(target: 'host' | 'join'): void {
    if (blockAttempt(target)) return;
    session.selectVersusForm();
    void navigateMenu(target);
  }

  async function confirmNavigation(
    message: string,
    actionLabel: string,
    destructive = false,
  ): Promise<boolean> {
    if (!$session.joined || ($session.phase !== 'playing' && $session.phase !== 'countdown') || !inOwnTurn)
      return true;
    const before = context();
    const accepted = await confirmation!.confirm(message, { destructive, actionLabel });
    return accepted && before.stillCurrent();
  }

  async function navigateMenu(target: Page | 'settings'): Promise<void> {
    if (settingsOpen || screen === target) return;
    if (
      target !== 'settings' &&
      target !== 'menu' &&
      blockAttempt(target === 'leaderboard' ? 'leaderboards' : target)
    )
      return;
    if (target !== 'settings' && !$session.inSetupLobby && ($session.creatingRoom || $session.joiningRoom))
      return;
    const title = SCREEN_TITLES[target];
    if (
      target !== 'settings' &&
      !(await confirmNavigation(
        `Open ${title}? The round will keep running while you are away.`,
        `Open ${title}`,
      ))
    )
      return;
    clearSelection();
    navigate({ type: 'open', page: target });
  }

  async function goBack(): Promise<void> {
    if (backToMode) {
      if ($session.mode === 'versus') goLobby();
      else chooseMode($session.mode);
      return;
    }
    await navigationWorkflow.backTo(backDestination(navigation, $session));
  }

  function closeSettings(): void {
    void navigationWorkflow.backTo(transition(navigation, { type: 'back' }));
  }

  function backFromProfileBlock(): void {
    attemptedBlockedFeature = null;
    navigationWorkflow.exitHome('detach');
  }

  async function goHome(): Promise<void> {
    if (
      !(await confirmNavigation(
        asyncRoom
          ? 'Leave your turn? Your clock keeps running and the room stays on your menu.'
          : 'Leave this round and return to the menu?',
        asyncRoom ? 'Leave Turn' : 'Leave Round',
        true,
      ))
    )
      return;
    exitRoom();
  }

  async function clearUserData(): Promise<void> {
    const accepted = await confirmation!.confirm(
      'Clear all Spellie data on this browser? This removes your name, preferences, room access, and local history. Server-side scores will not be deleted.',
      { destructive: true, actionLabel: 'Clear User Data' },
    );
    // Release the confirmation before clearing data or navigating away.
    await tick();
    if (!accepted) return;
    if (!store.clearUserData()) {
      showToast('Could not clear browser data. Use your browser’s site-data settings.');
      return;
    }
    queryClient.clear();
    session.stop();
    reloadAtRoot();
  }

  function saveWelcomeIdentity(identity: { name: string; avatar: Avatar }): void {
    session.setIdentity(identity);
    welcomeOpen = false;
    if (initialRoute.roomCode && $session.roomCode) void joinRoom();
  }

  onMount(() => {
    session.hydrate(initialRoute.roomCode ?? undefined);
    const stopPostMatch = postMatch.mount();
    if (initialRoute.roomCode) store.roomCode = $session.roomCode;
    navigationWorkflow.start();
    void session.loadProfile();
    const resume = (): void => {
      void session.refreshProfile();
      void refreshMyRooms(queryClient);
      if ($session.joined && !$session.reconnectStopped) {
        if ($session.connected) session.syncClock();
        else session.connect();
      }
    };
    const removeBrowserLifecycle = installBrowserLifecycle({
      resume,
      refreshProfile: () => void session.refreshProfile(),
      unlockAudio: () => Sfx.unlock(),
      suspendAudio: (hidden) => Sfx.setSuspended(hidden),
      navigateSound: () => Sfx.play('navigate'),
    });
    if (!$session.name) {
      welcomeOpen = true;
    } else if (initialRoute.roomCode && $session.roomCode) {
      void joinRoom(true);
    }
    return () => {
      navigationWorkflow.dispose();
      removeBrowserLifecycle();
      stopPostMatch();
      session.stop();
    };
  });
</script>

<ThemeBackground />
<div id="app">
  <PageNavigation
    practice={screen === 'game' && $session.mode === 'practice'}
    showFinish={screen === 'game'}
    finishing={finish.finishing}
    finishDisabled={!$session.ready || $session.spectator}
    {canPlay}
    showHint={() => gameScreen?.showHint()}
    finishRound={finishParticipation}
    atHome={screen === 'menu'}
    backDisabled={screen === 'results' && postMatch.gated}
    joined={$session.joined}
    connected={$session.connected}
    reconnecting={$session.joined && !$session.connected && !$session.reconnectStopped}
    {settingsOpen}
    back={goBack}
    home={goHome}
    settings={() => navigateMenu('settings')}
  />

  {#if screen === 'menu' || screen === 'solo' || screen === 'versus'}
    <MenuScreen {screen} {chooseMode} {navigateMenu} {openVersus} {openRoom} />
  {:else if screen === 'competitive'}
    <CompetitiveScreen
      explainRules={() => rules.explain(modeConfig('competitive').variant)}
      enterMatch={(code) => {
        session.setRoomInput(code);
        void joinRoom();
      }}
    />
  {:else if screen === 'leaderboard'}
    <section id="screen-leaderboard" class="screen submenu">
      <h1>Leaderboard</h1>
      {#if !profileBlockOpen}<LeaderboardPanel
          credential={$session.profileCredential}
          profileGeneration={$session.profileGeneration}
        />{/if}
    </section>
  {:else if screen === 'archive' && archivedRoundId}
    {#key archivedRoundId}
      <ArchivedResult
        rejectProfileCredential={session.rejectProfileCredential}
        roundId={archivedRoundId}
        result={explicitArchive ? null : $session.archivedResult}
        credential={$session.profileCredential}
        profileGeneration={$session.profileGeneration}
        getProfileCredential={session.getProfileCredential}
        openLeaderboard={() => navigateMenu('leaderboard')}
      />
    {/key}
  {:else if screen === 'loading'}
    <LoadingScreen
      busy={$session.creatingRoom || $session.joiningRoom || $session.joined}
      error={$session.joinStatus}
      retry={() => ($session.inSetupLobby ? session.prepareSetup() : goBack())}
    />
  {:else if screen === 'setup'}
    <ModeSetupScreen setupEditable={editability === 'setup'} {updateConfig} />
  {:else if screen === 'join' || screen === 'host'}
    <JoinRoomScreen {screen} {createRoom} joinRoom={() => joinRoom()} {updateConfig} />
  {:else if screen === 'lobby'}
    <RoomLobby
      mode={$session.mode}
      roomCode={$session.roomCode}
      players={shownPlayers}
      meId={$session.meId}
      {isHost}
      canKick={role === 'host'}
      config={$session.config}
      {settingsEditable}
      {updateConfig}
      inviteUrl={inviteUrl($session.roomCode)}
      {shareRoomLink}
      {startRound}
      {leaveRoom}
      {resetScores}
      kickPlayer={session.kickPlayer}
      chat={$session.chat}
      sendChat={session.sendChat}
      connected={$session.connected}
    />
  {:else if screen === 'turn-lobby'}
    <TurnLobby
      roomCode={$session.roomCode}
      round={$session.round}
      roundDuration={($session.activeConfig ?? $session.config).roundDuration}
      players={shownPlayers}
      meId={$session.meId}
      canKick={role === 'host'}
      spectator={$session.spectator}
      finished={$session.finished}
      hasResults={$session.results !== null}
      completed={asyncRoom && $session.phase === 'results' && $session.results !== null}
      {beginTurn}
      nextRound={startRound}
      viewResults={viewResultsRecap}
      inviteUrl={inviteUrl($session.roomCode)}
      {shareRoomLink}
      {leaveRoom}
      kickPlayer={session.kickPlayer}
    />
  {:else if screen === 'match-lobby'}
    <WaitingScreen title="Match" message="Waiting for your opponent to connect…" status />
  {:else if screen === 'waiting'}
    <WaitingScreen
      title="You're finished"
      message="Waiting for the other players. Final scores will appear when everyone finishes or time runs out."
    />
  {:else if screen === 'game'}
    {#if finish.error}<p role="alert">{finish.error}</p>{/if}
    <GameScreen
      {gameplayPerformance}
      finishing={finish.finishing}
      bind:lastCountdown
      bind:visualRoundId
      bind:this={gameScreen}
      bind:canPlay
    />
  {:else if screen === 'results' && $session.results}
    <RoundResultsView
      lifecycle={$session.resultLifecycle}
      credential={$session.profileCredential}
      profileGeneration={$session.profileGeneration}
      asyncRoom={$session.mode === 'versus' && asyncRoom}
      phase={$session.phase}
      results={$session.results}
      mode={$session.mode}
      meId={$session.meId}
      {isHost}
      hostName={$session.players.find((player) => player.host)?.name ?? 'the host'}
      presentation={postMatch}
      playAgain={$session.mode === 'competitive'
        ? () => chooseMode('competitive')
        : asyncRoom && $session.phase === 'playing'
          ? () => session.dismissResults()
          : startRound}
      backToLobby={goLobby}
      openLeaderboard={openPostMatchLeaderboard}
    />
  {/if}

  <Dialog
    open={settingsOpen}
    labelledby="settings-title"
    presentation="large"
    dismissOnBackdrop
    returnFocus={() => document.querySelector<HTMLElement>('.page-nav [aria-label="Settings"]')}
    onDismiss={closeSettings}
  >
    {#snippet header()}
      <DialogHeader
        id="settings-title"
        title="Settings"
        level={1}
        closeLabel="Close Settings"
        close={closeSettings}
      />
    {/snippet}
    {#if settingsOpen}
      <SettingsScreen
        name={$session.name}
        avatar={$session.avatar}
        getProfileCredential={session.getProfileCredential}
        saveName={(value) => session.setIdentity({ name: value })}
        saveAvatar={chooseAvatar}
        {clearUserData}
      />
      <ToastRegion
        id={toasts.id}
        message={toasts.message}
        good={toasts.good}
        dismiss={() => toasts.dismiss()}
      />
    {/if}
  </Dialog>
  <WelcomeDialog open={welcomeOpen} save={saveWelcomeIdentity} />

  <RulesDialog open={rules.open} variant={rules.variant} teaching={rules.teaching} closeRules={rules.close} />
  <ToastRegion
    id={toasts.id}
    message={screen === 'game' || settingsOpen ? '' : toasts.message}
    good={toasts.good}
    dismiss={() => toasts.dismiss()}
  />
  <ReadyCheckDialog
    prompt={readyCheckPrompt}
    readyCheck={$session.readyCheck}
    players={$session.players}
    serverNow={() => Date.now() + $session.serverOffset}
    confirm={session.confirmReady}
    cancel={session.cancelStart}
    start={startRound}
  />
  <ConfirmDialog bind:this={confirmation} />
  <ProfileBlockDialog
    open={profileBlockOpen}
    blockedUntil={$session.blockedUntil}
    blockMessage={$session.blockMessage}
    featureScoped={featureScopedBlock}
    backToHome={backFromProfileBlock}
    expired={session.profileBlockExpired}
  />
</div>

<style>
  #app {
    position: relative;
    z-index: 1;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: max(8px, env(safe-area-inset-top)) 14px max(12px, env(safe-area-inset-bottom));
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
  }
  :global(body.no-scroll) #app {
    height: 100dvh;
    min-height: 0;
  }
</style>
